import { Bell, PanelLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { Menubar, MenubarMenu, MenubarTrigger } from '@/components/ui-kit/menubar';
import { useSidebar } from '@/components/ui-kit/sidebar';
import { HistoryBackButton } from '@/components/core/history-back-button/history-back-button';
import { useEnsureSupportedLanguage } from '@/components/core/language-selector/language-selector';
import { Notification } from '@/components/core/notification/component/notification/notification';
import { ProfileMenu } from '@/components/core/profile-menu/profile-menu';
import { ThemeSwitcher } from '@/components/core/theme-switcher/theme-switcher';
import { cn } from '@/lib/utils';
import { getHrmShellBreadcrumbs, type HrmBreadcrumb } from './hrm-shell-breadcrumbs';

type NotificationsData = {
  notifications: unknown[];
  unReadNotificationsCount: number;
  totalNotificationsCount: number;
};

type HrmTopBarProps = {
  notifications: NotificationsData;
  /** The page has scrolled: the bar shows its shadow. */
  isScrolled?: boolean;
};

/** Only crumbs that are pages; links muted, the current page medium (Component Map › Breadcrumb). */
const HrmBreadcrumbs = ({ crumbs }: { crumbs: HrmBreadcrumb[] }) => {
  const { t } = useTranslation();
  if (crumbs.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden text-sm"
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return (
          // On a phone only the current page is shown.
          <span
            key={crumb.href ?? `${crumb.title}-${index}`}
            className={cn('flex min-w-0 items-center gap-1.5', !isLast && 'max-md:hidden')}
          >
            {index > 0 && (
              <span aria-hidden className="text-muted-foreground">
                ›
              </span>
            )}
            {crumb.href && !isLast ? (
              <Link
                to={crumb.href}
                className="whitespace-nowrap rounded-sm text-muted-foreground underline-offset-[3px] hover:text-foreground hover:underline"
              >
                {t(crumb.title)}
              </Link>
            ) : (
              <span
                className={cn(
                  'whitespace-nowrap',
                  isLast && 'truncate font-medium text-foreground'
                )}
                aria-current={isLast ? 'page' : undefined}
              >
                {t(crumb.title)}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
};

/**
 * The top bar (HR Module Prototype, Component Map › App shell): 48px, no bottom border, a shadow only
 * once the page scrolls. Left: the sidebar button (collapses the sidebar; opens it as a drawer on a
 * phone), Back and the breadcrumb. Right: notifications with an unread dot, the theme switch, and the
 * profile bubble. The prototype's Search (Soon) field is left out on purpose (agreed 2026-11-02).
 */
export const HrmTopBar = ({ notifications, isScrolled = false }: HrmTopBarProps) => {
  const { t } = useTranslation();
  const { toggleSidebar } = useSidebar();
  const { pathname } = useLocation();
  useEnsureSupportedLanguage();

  const breadcrumbItems = getHrmShellBreadcrumbs(pathname);

  return (
    <header
      data-testid="hrm-top-bar"
      data-scrolled={isScrolled}
      className={cn(
        'z-nav flex h-12 shrink-0 items-center gap-2 bg-background px-4 transition-shadow duration-200',
        isScrolled && 'shadow-[0_6px_16px_-12px_rgb(0_0_0/0.35)]'
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
        onClick={toggleSidebar}
        aria-label={t('TOGGLE_SIDEBAR')}
        title={t('TOGGLE_SIDEBAR')}
      >
        <PanelLeft className="size-[18px]" />
      </Button>
      <span aria-hidden className="h-4 w-px shrink-0 bg-border" />
      <HistoryBackButton />
      <HrmBreadcrumbs crumbs={breadcrumbItems} />

      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <Menubar className="h-auto border-none bg-transparent p-0 shadow-none">
          <MenubarMenu>
            <MenubarTrigger
              asChild
              className="cursor-pointer p-0 focus:bg-transparent data-[state=open]:bg-transparent"
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="relative size-8 text-muted-foreground hover:text-foreground"
                aria-label={t('NOTIFICATIONS')}
                title={t('NOTIFICATIONS')}
              >
                <Bell className="size-[18px]" />
                {notifications.unReadNotificationsCount > 0 && (
                  <span
                    aria-hidden
                    className="absolute right-1.5 top-1.5 size-2 rounded-full bg-destructive shadow-[0_0_0_2px_oklch(var(--background))]"
                  />
                )}
              </Button>
            </MenubarTrigger>
            <Notification />
          </MenubarMenu>
        </Menubar>

        <ThemeSwitcher />
        <span aria-hidden className="h-4 w-px shrink-0 bg-border" />
        <ProfileMenu />
      </div>
    </header>
  );
};
